import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { type ShipmentUnitDTO, type ShippingLineDTO } from '@uturn/api/v1';
import { HiddenFieldLabelInfo } from '@uturn/portal/modules/shipments/components/hidden-field-label-info';
import { ShipmentDetailContext } from '@uturn/portal/modules/shipments/providers/shipment-detail';
import type { FormValues } from '@uturn/portal/modules/shipments/schema';
import {
	grossWeightUndefined,
	isFieldReadOnly,
	shouldRevalidate,
	shouldUpdate,
} from '@uturn/portal/modules/shipments/utils';
import { useContainerNumberValidation } from '@uturn/portal/modules/shipments/validation';
import {
	isSelectableContainerType,
	type SelectableContainerType,
} from '@uturn/portal/types/container-type.typeguard';
import {
	ContainerStatus,
	TransportTypeCode,
} from '@uturn/portal/types/shipment';
import {
	isSelectableShippingLine,
	type SelectableShippingLine,
} from '@uturn/portal/types/shipping-line.typeguard';
import {
	Checkbox,
	Combobox,
	FormControl,
	FormDescription,
	FormField,
	FormFieldReadOnly,
	FormItem,
	FormLabel,
	FormMessage,
	Icon,
	Input,
	RadioGroup,
	RadioGroupItem,
} from '@uturn/ui-kit';
import { useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export function ContainerDetails({
	shipmentNumber = 0,
	containerTypesRaw,
	shippingLinesRaw,
	enableAddingGroup,
}: {
	shipmentNumber?: number;
	containerTypesRaw: ShipmentUnitDTO[];
	shippingLinesRaw: ShippingLineDTO[];
	enableAddingGroup: boolean;
}) {
	const { t } = useTranslation();
	const { validateContainerNumber, validateContainerNumberDuplicate } =
		useContainerNumberValidation();
	const { readOnlyFields } = useContext(ShipmentDetailContext);
	const form = useFormContext<FormValues>();

	const transportType = form.getValues('transportType');
	const tareWeight = form.getValues('tareWeight.quantity');

	const watchContainerType = form.watch('containerType');
	const watchContainerNumber = form.watch('equipment.containerNumber');
	const watchShippingLineCode = form.watch('shippingLine.code');
	const watchGrossWeight = form.watch('grossWeight.quantity');

	const [lastPositiveGrossWeight, setLastPositiveGrossWeight] =
		useState(watchGrossWeight);

	useEffect(() => {
		if ((watchGrossWeight ?? 0) > 0) {
			setLastPositiveGrossWeight(watchGrossWeight);
		}
	}, [watchGrossWeight]);

	const ContainerStatusLabels: Record<ContainerStatus, string> = {
		[ContainerStatus.FULL]: t(
			`pages.addshipment.manual.container_status.${ContainerStatus.FULL}.label`,
			'Full',
		),
		[ContainerStatus.EMPTY]: t(
			`pages.addshipment.manual.container_status.${ContainerStatus.EMPTY}.label`,
			'Empty',
		),
	};

	const showGeneratorSet = () => {
		return !watchContainerType
			? false
			: containerTypesRaw.find((item) =>
					[item.code, item.description].includes(watchContainerType),
				)?.generatorSetRequired || false;
	};

	const handleShippingLineCodeChange = (
		shippingLine: SelectableShippingLine,
	) => {
		form.setValue('shippingLineId', shippingLine.id, shouldUpdate);
		form.setValue('shippingLine', shippingLine, shouldUpdate);

		const results = validateContainerNumber(
			watchContainerNumber ?? '',
			shippingLine.code,
		);

		if (!results.valid) {
			results.issues.forEach(({ path, message }) =>
				form.setError(path, { type: 'custom', message }),
			);
		}

		return results;
	};

	const handleContainerTypeChange = (
		containerType: SelectableContainerType,
	) => {
		form.setValue('equipment.shipmentUnitId', containerType.id, shouldUpdate);

		if (!containerType.portbaseCode) {
			return;
		}

		form.setValue(
			'equipment.isoType',
			containerType.portbaseCode,
			shouldRevalidate,
		);
	};

	const handleContainerNumberBlur = async (containerNumber: string) => {
		form.clearErrors('equipment.containerNumber');

		if (!form.formState.dirtyFields.equipment?.containerNumber) {
			return;
		}

		let results = validateContainerNumber(
			containerNumber,
			watchShippingLineCode,
		);

		if (!results.valid) {
			results.issues.forEach(({ path, message }) =>
				form.setError(path, { type: 'custom', message }),
			);

			return;
		}

		results = await validateContainerNumberDuplicate(
			containerNumber,
			shipmentNumber,
			results,
		);

		if (!results.valid) {
			results.issues.forEach(({ path, message }) =>
				form.setError(path, { type: 'custom', message }),
			);
		}

		return results;
	};

	const handleContainerStatusChange = (containerStatus: string) => {
		switch (containerStatus) {
			case ContainerStatus.EMPTY:
				form.setValue('grossWeight.quantity', 0, shouldUpdate);
				break;
			case ContainerStatus.FULL:
				form.setValue(
					'grossWeight.quantity',
					lastPositiveGrossWeight || grossWeightUndefined,
					shouldUpdate,
				);
				break;
			default:
				break;
		}
	};

	return (
		<div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-6">
			<div className="sm:col-span-full">
				<h3 className="font-heading text-lg font-medium tracking-tight">
					{t(
						'pages.create_shipment.shipment-details.container_details.heading',
						'Container details',
					)}
				</h3>
			</div>
			<div className="col-span-3 grid grid-cols-1 gap-6 sm:grid-cols-6">
				<div className="sm:col-span-4">
					<FormField
						control={form.control}
						name="containerType"
						render={({ field }) => (
							<FormItem>
								<FormLabel className="required-asterix">
									{t(
										'pages.shipment-details.container_details.container_type.label',
										'Container type',
									).toString()}
								</FormLabel>
								{isFieldReadOnly(readOnlyFields, 'containerType') ? (
									<FormFieldReadOnly value={field.value} />
								) : (
									<FormControl>
										<Combobox
											ref={field.ref}
											defaultValue={field.value} // containerType.code
											onChange={field.onChange}
											options={containerTypesRaw
												.filter(isSelectableContainerType)
												.map((item) => ({
													value: item.code,
													secondaryValue: item.description,
													label: item.description,
													...(item.portbaseCode && {
														keywords: [item.portbaseCode], // Allow searching by Iso Type.
													}),
													onSelect: () => handleContainerTypeChange(item),
												}))}
											className="w-full"
											placeholder={t(
												'pages.shipment-details.container_details.container_type.placeholder',
												'Select type',
											).toString()}
											searchPlaceholder={t(
												'pages.shipment-details.container_details.container_type.search.placeholder',
												'Search container type...',
											).toString()}
											emptyMessage={t(
												'pages.shipment-details.container_details.container_type.search.empty',
												'No container type found.',
											).toString()}
										/>
									</FormControl>
								)}
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>
				<div className="sm:col-span-2">
					<FormField
						control={form.control}
						name="equipment.isoType"
						render={({ field }) => (
							<FormItem>
								<FormLabel className="required-asterix">
									{t(
										'pages.shipment-details.container_details.iso_type.label',
										'ISO-type',
									)}
								</FormLabel>
								{isFieldReadOnly(readOnlyFields, 'equipment.isoType') ? (
									<FormFieldReadOnly value={field.value} />
								) : (
									<FormControl>
										<Input
											{...field}
											disabled={!watchContainerType}
											onBlur={
												(e) => form.trigger('equipment.isoType') // TODO: Fix this workaround
											}
										/>
									</FormControl>
								)}
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>
				{showGeneratorSet() && (
					<div className="sm:col-span-full">
						<FormField
							control={form.control}
							name="generatorSet"
							render={({ field }) => (
								<FormItem className="flex flex-row items-center space-x-3 space-y-0">
									<FormControl>
										<Checkbox
											checked={field.value}
											onCheckedChange={field.onChange}
											disabled={isFieldReadOnly(readOnlyFields, 'generatorSet')}
										/>
									</FormControl>
									<div className="space-y-1 leading-none">
										<FormLabel>
											{t(
												'pages.addshipment.manual.generator_set.label',
												'Generator set required',
											).toString()}
										</FormLabel>
									</div>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				)}
				{transportType === TransportTypeCode.SHUNT && (
					<div className="sm:col-span-full">
						<FormField
							control={form.control}
							name="containerStatus"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t(
											'pages.addshipment.manual.container_status.label',
											'Container status',
										)}
									</FormLabel>
									{isFieldReadOnly(readOnlyFields, 'containerStatus') ? (
										<FormFieldReadOnly
											value={ContainerStatusLabels[field.value]}
										/>
									) : (
										<FormControl>
											<RadioGroup
												{...field}
												onValueChange={(e) => {
													field.onChange(e);
													handleContainerStatusChange(e);
												}}
												value={field.value}
												className="flex flex-col space-y-1"
											>
												<FormItem className="flex items-center space-x-3 space-y-0">
													<FormControl>
														<RadioGroupItem value="full" />
													</FormControl>
													<FormLabel className="cursor-pointer font-normal">
														{t(
															'pages.addshipment.manual.container_status.full.label',
															'Full',
														)}
													</FormLabel>
												</FormItem>
												<FormItem className="flex items-center space-x-3 space-y-0">
													<FormControl>
														<RadioGroupItem value="empty" />
													</FormControl>
													<FormLabel className="cursor-pointer font-normal">
														{t(
															'pages.addshipment.manual.container_status.empty.label',
															'Empty',
														)}
													</FormLabel>
												</FormItem>
											</RadioGroup>
										</FormControl>
									)}
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				)}
				<div className="sm:col-span-6">
					<FormField
						control={form.control}
						name="shippingLine.code"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									{t(
										'pages.shipment-details.container_details.shipping_line.label',
										'Shipping Line',
									)}
								</FormLabel>
								{isFieldReadOnly(readOnlyFields, 'shippingLine') ? (
									<FormFieldReadOnly value={field.value ?? ''} />
								) : (
									<FormControl>
										<Combobox
											ref={field.ref}
											defaultValue={field.value} // shippingLine.code
											onChange={field.onChange}
											options={shippingLinesRaw
												.filter(isSelectableShippingLine)
												.map((item) => ({
													value: item.code,
													label: `${item.description} (${item.code})`,
													onSelect: () => handleShippingLineCodeChange(item),
												}))}
											placeholder={t(
												'pages.shipment-details.container_details.shipping_line.placeholder',
												'Select shipping line',
											).toString()}
											searchPlaceholder={t(
												'pages.shipment-details.container_details.shipping_line.search.placeholder',
												'Type to search shipping lines...',
											).toString()}
											emptyMessage={t(
												'pages.shipment-details.container_details.shipping_line.search.empty',
												'No shipping line found.',
											).toString()}
										/>
									</FormControl>
								)}
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>
				<div className="sm:col-span-full">
					<FormField
						control={form.control}
						name="equipment.containerNumber"
						render={({ field }) => (
							<FormItem>
								<FormLabel className="flex items-center">
									{t(
										'pages.shipment-details.container_details.container_number.label',
										'Container number',
									)}
									<HiddenFieldLabelInfo />
								</FormLabel>
								{isFieldReadOnly(
									readOnlyFields,
									'equipment.containerNumber',
								) ? (
									<FormFieldReadOnly value={field.value ?? ''} />
								) : (
									<>
										<FormControl>
											<Input
												{...field}
												value={field.value}
												onChange={(e) =>
													field.onChange(e.target.value.toLocaleUpperCase())
												}
												onBlur={(e) =>
													handleContainerNumberBlur(
														e.target.value.toLocaleUpperCase(),
													)
												}
												disabled={enableAddingGroup}
												placeholder={t(
													'components.shipment.hidden_field.placeholder_short',
													'Hidden until the shipment is matched',
												).toString()}
											/>
										</FormControl>
										{enableAddingGroup && (
											<FormDescription>
												<Icon icon={faInfoCircle} />{' '}
												{t(
													'pages.create_grouped_shipment.shipment_details.container_details.container_number.info',
													'Add container numbers after creation',
												)}
											</FormDescription>
										)}
									</>
								)}
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>
				<div className="sm:col-span-full">
					<FormField
						control={form.control}
						name="seal"
						render={({ field }) => (
							<FormItem>
								<FormLabel className="flex items-center">
									{t(
										'pages.shipment-details.container_details.seal.label',
										'Seal',
									)}
									<HiddenFieldLabelInfo />
								</FormLabel>
								{isFieldReadOnly(readOnlyFields, 'seal') ? (
									<FormFieldReadOnly value={field.value ?? ''} />
								) : (
									<FormControl>
										<Input
											{...field}
											placeholder={t(
												'components.shipment.hidden_field.placeholder_short',
												'Hidden until the shipment is matched',
											).toString()}
										/>
									</FormControl>
								)}
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>
				{!!tareWeight && (
					<div className="sm:col-span-full">
						<FormField
							control={form.control}
							name="tareWeight.quantity"
							render={({ field }) => (
								<FormItem className="relative">
									<FormLabel>
										{t(
											'pages.shipment-details.container_details.tare_weight.label',
											'Tare weight',
										)}
									</FormLabel>
									<FormFieldReadOnly
										value={`${field.value} ${t(
											'general.shorthands.units.kilogram',
											'kg',
										)}`}
									/>
								</FormItem>
							)}
						/>
					</div>
				)}
			</div>
		</div>
	);
}
